import { Injectable, inject, Injector } from '@angular/core';

import type { TableData } from '../models/chart-data.model';
import type { TableCardConfig } from '../models/table-config.model';
import {
	hasRenderableTablePayload,
	tableDataFromChartDataResponse,
} from '../components/editors/jepto-table-editor/services/table-preview-data.util';
import { DataService } from '../services/data.service';
import { PageDataRefreshCoordinatorService } from '../services/page-data-refresh-coordinator.service';
import { PageFilterService } from '../services/page-filter.service';
import { UlidService } from '../services/ulid.service';
import { CardStateService } from '../states/card-state.service';
import { TableCardInteractionService } from '../services/table-card-interaction.service';
import { isTableCardConfig } from '../services/card-config-change.types';
import type { TableRemoteFetchResult } from '../models/table-grid-query.types';
import { warn } from '@jepto/shared/utils/logger';
import {
	buildTableRemoteFetchResponse,
	cachedPayloadMatchesOffset,
	canServeCachedTableSlice,
	emptyTableRemoteFetchResponse,
	isFetchAborted,
	isPagingBusyError,
	offsetLimitToPage,
	sortParamsFromQuerying,
	tableOrderMatchesSort,
	toRemoteColumns,
} from './table-remote-fetch-decision.util';
import { resolveTableOrderMemberKey } from './table-column.util';

export {
	cachedPayloadMatchesOffset,
	canServeSliceFromCachedColumns,
	extractPageFromColumnarData,
	offsetLimitToPage,
	resolveTotalRowCount,
	sliceColumnsForGridRequest,
	tableSliceMatchesRequest,
} from './table-remote-fetch-decision.util';

/**
 * Coordinates Grid Pro `fetchCallback` with host refetch (`getCardData` / WebSocket).
 */
@Injectable({ providedIn: 'root' })
export class TableRemoteFetchBridge {
	private readonly dataService = inject(DataService);
	private readonly pageFilterService = inject(PageFilterService);
	private readonly pageDataRefreshCoordinator = inject(
		PageDataRefreshCoordinatorService
	);
	private readonly ulidService = inject(UlidService);
	private readonly cardState = inject(CardStateService);
	private readonly injector = inject(Injector);

	#interaction(): TableCardInteractionService {
		return this.injector.get(TableCardInteractionService);
	}

	resolveTableData(cardId: string): TableData | undefined {
		if (!cardId) {
			throw new Error(
				'[TableRemoteFetchBridge] resolveTableData requires cardId'
			);
		}
		const fromTable = this.dataService.tableDataSignal(cardId)();
		if (fromTable && hasRenderableTablePayload(fromTable)) {
			return fromTable;
		}
		const fromChart = tableDataFromChartDataResponse(
			this.dataService.cardDataSignal(cardId)(),
			cardId
		);
		return fromChart && hasRenderableTablePayload(fromChart)
			? fromChart
			: undefined;
	}

	toRemoteColumns(
		data: TableData,
		config?: TableCardConfig
	): Record<string, Array<string | number | boolean | null>> {
		return toRemoteColumns(data, config);
	}

	// fallow-ignore-next-line unused-class-member
	async fetchSlice(params: {
		cardId: string;
		offset: number;
		limit: number;
		querying: unknown;
		signal?: AbortSignal;
		config?: TableCardConfig;
	}): Promise<TableRemoteFetchResult> {
		const {
			cardId,
			offset,
			limit,
			querying,
			signal,
			config: liveConfig,
		} = params;
		const config = liveConfig ?? this.cardState.getCard(cardId);
		const cached = this.resolveTableData(cardId);
		if (!config) {
			if (cached && hasRenderableTablePayload(cached)) {
				return buildTableRemoteFetchResponse({
					data: cached,
					offset,
					limit,
					window: 'absolute-slice',
				});
			}
			throw new Error(
				`[TableRemoteFetchBridge] No card config in CardStateService for cardId="${cardId}"`
			);
		}
		if (!isTableCardConfig(config)) {
			throw new Error(
				`[TableRemoteFetchBridge] Card "${cardId}" is not a table card (cardType="${config.cardType}")`
			);
		}

		const emptyResponse = emptyTableRemoteFetchResponse(config);
		if (emptyResponse) {
			return emptyResponse;
		}

		const paging = offsetLimitToPage(offset, limit);
		const sortRaw = sortParamsFromQuerying(querying);
		const sortFromGrid = sortRaw?.column
			? {
					...sortRaw,
					column: resolveTableOrderMemberKey(config, sortRaw.column),
				}
			: sortRaw;

		const sortMismatch =
			!!sortFromGrid &&
			!tableOrderMatchesSort(config.data?.order, sortFromGrid);
		const filtersActive = this.pageFilterService.hasActiveFilters();
		const pageId = this.#pageIdForFilteredFetch(config);
		const refreshFloor =
			this.pageDataRefreshCoordinator.resolveRefreshFloor(pageId);
		const hasStaleCacheForRefreshBatch =
			!!refreshFloor &&
			!!cached &&
			hasRenderableTablePayload(cached) &&
			!this.#tableDataMatchesPageRefreshFloor(cached, pageId);
		const needsPageRefreshBatchWait =
			filtersActive || hasStaleCacheForRefreshBatch;

		// Serve in-memory WS/DataService slices only when they already match this
		// offset+limit (page load / remount). Page-size growth and other misses
		// fall through to getCardData so Grid waits on the live WS event.
		// With active page filters (or stale pre-batch cache after clear), skip
		// stale slices — wait for getPageData WebSocket payloads on/after the floor.
		// Page 2+ and sort still fall through to getCardData via applyGridQueryAndWait.
		if (cached && !sortMismatch && !needsPageRefreshBatchWait) {
			let cachedDecision = canServeCachedTableSlice({
				config,
				data: cached,
				offset,
				limit,
				sortFromGrid,
				pagingBusy: false,
			});
			if (
				cachedDecision === 'miss' &&
				this.#interaction().isPagingLoading(cardId)
			) {
				cachedDecision = canServeCachedTableSlice({
					config,
					data: cached,
					offset,
					limit,
					sortFromGrid,
					pagingBusy: true,
				});
			}
			if (cachedDecision !== 'miss') {
				return buildTableRemoteFetchResponse({
					data: cached,
					config,
					offset,
					limit,
					window: cachedDecision,
				});
			}
		}

		if (signal?.aborted) {
			throw new DOMException('Aborted', 'AbortError');
		}

		if (needsPageRefreshBatchWait) {
			const filteredCached =
				cached &&
				hasRenderableTablePayload(cached) &&
				this.#tableDataMatchesPageRefreshFloor(cached, pageId)
					? cached
					: await this.#waitForPageFilteredTableData(
							cardId,
							signal,
							pageId
						);
			if (filteredCached) {
				let filteredDecision = canServeCachedTableSlice({
					config,
					data: filteredCached,
					offset,
					limit,
					sortFromGrid,
					pagingBusy: false,
					allowExplicitEmptyPage: true,
				});
				if (
					filteredDecision === 'miss' &&
					this.#interaction().isPagingLoading(cardId)
				) {
					filteredDecision = canServeCachedTableSlice({
						config,
						data: filteredCached,
						offset,
						limit,
						sortFromGrid,
						pagingBusy: true,
						allowExplicitEmptyPage: true,
					});
				}
				if (filteredDecision !== 'miss') {
					return buildTableRemoteFetchResponse({
						data: filteredCached,
						config,
						offset,
						limit,
						window: filteredDecision,
					});
				}
			} else if (!sortMismatch && offset === 0) {
				return buildTableRemoteFetchResponse({
					data: {
						cardType: 'table',
						data: {
							pagination: { totalRows: 0 },
							result: { columns: {} },
						},
					} as TableData,
					config,
					offset,
					limit,
					window: 'first-page',
				});
			}
		}

		// Competing fetchCallback (e.g. page 1) while page 2 getCardData is in
		// flight must not start another fetch — that cancels the in-flight wait.
		if (this.#interaction().hasInFlightSliceWait(cardId)) {
			if (
				cached &&
				hasRenderableTablePayload(cached) &&
				cachedPayloadMatchesOffset(cached, offset)
			) {
				return buildTableRemoteFetchResponse({
					data: cached,
					config,
					offset,
					limit,
					window: 'first-page',
				});
			}
			const inFlight =
				await this.#interaction().awaitInFlightSliceWait(cardId);
			if (signal?.aborted) {
				throw new DOMException('Aborted', 'AbortError');
			}
			const fromWait =
				inFlight && hasRenderableTablePayload(inFlight)
					? inFlight
					: this.resolveTableData(cardId);
			if (
				fromWait &&
				hasRenderableTablePayload(fromWait) &&
				cachedPayloadMatchesOffset(fromWait, offset)
			) {
				const latestConfig =
					liveConfig ??
					(this.cardState.getCard(cardId) as
						| TableCardConfig
						| undefined) ??
					config;
				return buildTableRemoteFetchResponse({
					data: fromWait,
					config: latestConfig,
					offset,
					limit,
					window: 'first-page',
				});
			}
			throw new Error(
				`[TableRemoteFetchBridge] Paging in flight without rows for "${cardId}"`
			);
		}

		try {
			const fresh = sortMismatch
				? await this.#interaction().applyGridQueryAndWait(cardId, {
						sort: sortFromGrid,
					})
				: await this.#interaction().applyGridQueryAndWait(cardId, {
						paging,
					});
			if (signal?.aborted) {
				throw new DOMException('Aborted', 'AbortError');
			}
			const latestConfig =
				liveConfig ??
				(this.cardState.getCard(cardId) as
					| TableCardConfig
					| undefined) ??
				config;
			return buildTableRemoteFetchResponse({
				data: fresh,
				config: latestConfig,
				offset,
				limit,
				window: 'first-page',
			});
		} catch (error) {
			if (isFetchAborted(signal, error)) {
				throw error;
			}
			if (isPagingBusyError(error)) {
				const fallbackBusy = this.resolveTableData(cardId);
				if (
					fallbackBusy &&
					cachedPayloadMatchesOffset(fallbackBusy, offset)
				) {
					return buildTableRemoteFetchResponse({
						data: fallbackBusy,
						config,
						offset,
						limit,
						window: 'first-page',
					});
				}
			}
			// Highcharts RemoteDataProvider maps thrown errors to an empty chunk and
			// `getPrePaginationRowCount()` then becomes 0 → "Showing 0–0 of 0".
			warn(
				'[TableRemoteFetchBridge] Page fetch failed; keeping last known rows',
				{ cardId, offset, limit, error }
			);
			const fallback = this.resolveTableData(cardId);
			if (fallback && hasRenderableTablePayload(fallback)) {
				return buildTableRemoteFetchResponse({
					data: fallback,
					config,
					offset,
					limit,
					window: 'first-page',
				});
			}
			throw error;
		}
	}

	#tableDataMatchesPageRefreshFloor(
		data: TableData,
		pageId: string | undefined
	): boolean {
		const floor = this.pageDataRefreshCoordinator.resolveRefreshFloor(pageId);
		if (!floor) {
			return false;
		}
		const requestId =
			'requestId' in data && typeof data.requestId === 'string'
				? data.requestId
				: undefined;
		if (!requestId) {
			return false;
		}
		if (
			!this.ulidService.extractInfo(requestId) ||
			!this.ulidService.extractInfo(floor)
		) {
			return false;
		}
		return this.ulidService.compare(requestId, floor) >= 0;
	}

	#pageIdForFilteredFetch(config: TableCardConfig): string | undefined {
		return (
			config.pageId?.trim() ||
			this.pageFilterService.getActivePageId()?.trim() ||
			undefined
		);
	}

	async #waitForPageFilteredTableData(
		cardId: string,
		signal: AbortSignal | undefined,
		pageId: string | undefined
	): Promise<TableData | undefined> {
		const deadline = Date.now() + 30_000;
		while (Date.now() < deadline) {
			if (signal?.aborted) {
				throw new DOMException('Aborted', 'AbortError');
			}
			const data = this.resolveTableData(cardId);
			if (
				data &&
				hasRenderableTablePayload(data) &&
				this.#tableDataMatchesPageRefreshFloor(data, pageId)
			) {
				return data;
			}
			await new Promise<void>(resolve => setTimeout(resolve, 50));
		}
		return undefined;
	}
}
